import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Section, Eyebrow, FadeIn } from "../../components/public/Section";
import { PageHero } from "./About";
import { Sparkles, Check, ArrowRight, Calculator } from "lucide-react";

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [guests, setGuests] = useState(150);
  const [pkgId, setPkgId] = useState("");

  useEffect(() => {
    api.get("/public/packages").then(r => { setPackages(r.data); setPkgId(r.data?.[1]?.id || r.data?.[0]?.id); });
  }, []);

  const selected = useMemo(() => packages.find(p => p.id === pkgId), [packages, pkgId]);
  const estimate = selected ? selected.price * guests : 0;
  const allInclusions = useMemo(() => {
    const set = new Set();
    packages.forEach(p => (p.inclusions || []).forEach(i => set.add(i)));
    return Array.from(set);
  }, [packages]);

  return (
    <>
      <PageHero title="Royal Packages" subtitle="Five canvases, one promise — hospitality of palaces." image="https://images.unsplash.com/photo-1696940823960-ee5242bddc47?auto=format&fit=crop&w=2000&q=80" />

      <Section>
        <Eyebrow>Choose Your Canvas</Eyebrow>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-6">
          {packages.map((p, i) => (
            <FadeIn key={p.id} delay={i * 0.06}>
              <div className={`p-7 h-full border ${p.is_signature ? "border-gold bg-gradient-to-b from-gold/10 to-transparent" : "border-navy-900/15"} hover:border-gold/80 transition`} data-testid={`pkg-${p.slug}`}>
                {p.is_signature && <div className="text-[9px] tracking-[0.25em] uppercase bg-gold text-navy-900 inline-block px-2 py-1 font-bold mb-3">Signature</div>}
                <div className="serif text-2xl">{p.name}</div>
                <div className="label-eyebrow opacity-80 mt-1">{p.tagline}</div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="serif text-5xl text-gold">₹{p.price}</span>
                  <span className="text-navy-900/50 text-xs">/plate</span>
                </div>
                <p className="text-navy-900/60 text-sm mt-4 leading-relaxed line-clamp-3">{p.description}</p>
                <ul className="mt-5 space-y-1.5 text-[12.5px] text-navy-900/55">
                  {(p.inclusions || []).map((inc, j) => (
                    <li key={j} className="flex gap-2"><Check size={13} className="text-gold mt-0.5 shrink-0" />{inc}</li>
                  ))}
                </ul>
                <div className="mt-5 text-xs tracking-wider text-navy-900/55">Ideal: {p.ideal_for}</div>
                <Link to="/contact" className="btn-gold mt-6 w-full justify-center" data-testid={`pkg-cta-${p.slug}`}>
                  Inquire <ArrowRight size={12} />
                </Link>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Comparison */}
      <Section dark>
        <Eyebrow dark>Comparison Table</Eyebrow>
        <h2 className="serif text-ivory text-4xl md:text-5xl mb-10">Side-by-side <em className="text-shimmer not-italic">at a glance.</em></h2>
        <div className="overflow-x-auto border border-gold/20">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-navy-800">
                <th className="text-left p-4 text-gold tracking-[0.2em] uppercase text-[11px]">Inclusion</th>
                {packages.map(p => <th key={p.id} className="p-4 text-gold tracking-[0.2em] uppercase text-[11px]">{p.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {allInclusions.map((inc, i) => (
                <tr key={i} className="border-t border-gold/10">
                  <td className="p-4 text-ivory/85">{inc}</td>
                  {packages.map(p => (
                    <td key={p.id} className="p-4 text-center">
                      {(p.inclusions || []).includes(inc) ? <Check size={16} className="text-gold inline" /> : <span className="text-ivory/20">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-gold/30 bg-navy-800">
                <td className="p-4 text-ivory serif text-lg">Price / plate</td>
                {packages.map(p => <td key={p.id} className="p-4 text-center serif text-2xl text-gold">₹{p.price}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Quote calculator */}
      <Section testId="quote-calc-section">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow>Concierge Estimate</Eyebrow>
            <h2 className="serif text-4xl md:text-6xl leading-tight">
              Quick <em className="text-shimmer not-italic">quote calculator.</em>
            </h2>
            <p className="text-navy-900/50 mt-4 max-w-md">Select a package and guest count for an indicative estimate. Final price is bespoke and includes décor, staff & menu personalisation.</p>
          </div>
          <div className="border border-gold/30 bg-white p-8" data-testid="quote-calculator">
            <div className="flex items-center gap-3 mb-5">
              <Calculator size={22} className="text-gold" />
              <div className="serif text-2xl">Estimate</div>
            </div>
            <label className="label">Package</label>
            <select className="input" value={pkgId} onChange={e => setPkgId(e.target.value)} data-testid="calc-package">
              {packages.map(p => <option key={p.id} value={p.id}>{p.name} · ₹{p.price}/plate</option>)}
            </select>
            <label className="label mt-4">Guest Count: <span className="text-gold">{guests}</span></label>
            <input type="range" min="50" max="2000" step="10" value={guests} onChange={e => setGuests(parseInt(e.target.value))} className="w-full accent-[#D4AF37]" data-testid="calc-guests" />
            <div className="mt-8 border-t border-gold/20 pt-6">
              <div className="label-eyebrow">Indicative Total</div>
              <div className="serif text-5xl text-navy-900 mt-2">
                ₹{estimate.toLocaleString("en-IN")}
              </div>
              <div className="text-navy-900/60 text-xs mt-2">+ GST · décor / themed add-ons priced bespoke</div>
            </div>
            <Link to="/contact" className="btn-gold w-full justify-center mt-6" data-testid="calc-inquire">
              Convert to a Proposal <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
